import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PublicDonation.module.css';

function canvasToFile(canvas: HTMLCanvasElement, name: string): Promise<File> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(new File([blob!], name, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.9);
  });
}

interface Props {
  onCapture: (file: File) => void;
  onClose: () => void;
}

function errorToI18nKey(err: unknown): string {
  if (err instanceof DOMException || err instanceof Error) {
    switch (err.name) {
      case 'NotReadableError':
      case 'TrackStartError':
        return 'donations.offline.cameraErrorInUse';
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return 'donations.offline.cameraErrorDenied';
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return 'donations.offline.cameraErrorNotFound';
    }
  }
  return 'donations.offline.cameraErrorGeneric';
}

function stopStream(stream: MediaStream | null) {
  stream?.getTracks().forEach((t) => t.stop());
}

const CameraCapture = ({ onCapture, onClose }: Props) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [showLive, setShowLive] = useState(true);

  useEffect(() => {
    if (!showLive) return;

    let cancelled = false;
    let localStream: MediaStream | null = null;

    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          if (!cancelled) setErrorKey('donations.offline.cameraErrorGeneric');
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
        });
        if (cancelled) {
          stopStream(stream);
          return;
        }
        localStream = stream;
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          video.play().then(() => { if (!cancelled) setReady(true); }).catch(() => {});
        }
      } catch (err) {
        if (!cancelled) setErrorKey(errorToI18nKey(err));
      }
    })();

    return () => {
      cancelled = true;
      stopStream(localStream);
      stopStream(streamRef.current);
      streamRef.current = null;
    };
  }, [showLive]);

  const capture = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0);
    stopStream(streamRef.current);
    streamRef.current = null;
    setShowLive(false);
    const file = await canvasToFile(canvas, `capture-${Date.now()}.jpg`);
    setCapturedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  }, []);

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setCapturedFile(null);
    setReady(false);
    setShowLive(true);
  };

  const accept = () => {
    if (capturedFile) onCapture(capturedFile);
  };

  const handleClose = () => {
    stopStream(streamRef.current);
    streamRef.current = null;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    onClose();
  };

  if (errorKey) {
    return (
      <div className={styles.cameraOverlay}>
        <div className={styles.cameraBox}>
          <div className={styles.cameraErrorContent}>
            <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p className={styles.cameraError}>{t(errorKey)}</p>
          </div>
          <button type="button" className={styles.secondaryBtn} onClick={handleClose}>{t('close')}</button>
        </div>
      </div>
    );
  }

  if (previewUrl) {
    return (
      <div className={styles.cameraOverlay}>
        <div className={styles.cameraBox}>
          <img src={previewUrl} alt="" className={styles.cameraVideo} />
          <div className={styles.cameraActions}>
            <button type="button" className={styles.submitBtn} onClick={accept}>
              {t('donations.offline.usePhoto')}
            </button>
            <button type="button" className={styles.secondaryBtn} onClick={retake}>
              {t('donations.offline.retakePhoto')}
            </button>
            <button type="button" className={styles.secondaryBtn} onClick={handleClose}>
              {t('cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.cameraOverlay}>
      <div className={styles.cameraBox}>
        <video ref={videoRef} autoPlay playsInline muted className={styles.cameraVideo} />
        <div className={styles.cameraActions}>
          <button type="button" className={styles.submitBtn} onClick={capture} disabled={!ready}>
            {t('donations.offline.capturePhoto')}
          </button>
          <button type="button" className={styles.secondaryBtn} onClick={handleClose}>
            {t('cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CameraCapture;
